(() => {
  if (window.customElements.get('tab-drawer-v2')) return;

  const SCROLL_LOCK_CLASS = 'tab-drawer-v2-scroll-lock';
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class TabDrawerV2 extends HTMLElement {
    constructor() {
      super();
      this.tabs = [];
      this.activeIndex = -1;
      this.previousFocus = null;
      this.boundOnKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.drawer = this.querySelector('.tab-drawer-v2__drawer');
      this.overlay = this.querySelector('.tab-drawer-v2__overlay');
      this.panel = this.querySelector('[data-tdv2-panel]');
      this.activeTitleEl = this.querySelector('[data-tdv2-active-title]');
      this.triggerList = this.querySelector('[data-tdv2-trigger-list]');
      this.navList = this.querySelector('[data-tdv2-nav-list]');
      this.iconArrow = this.iconHTML('[data-tdv2-icon-arrow]');
      if (!this.drawer || !this.overlay || !this.panel) return;

      this.collectTabs();
      this.renderTriggers();
      this.renderNav();
      this.bindCloseHandlers();
      this.setupRabatte();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.boundOnKeydown);
      if (this.boundRabatteChange) {
        document.removeEventListener('change', this.boundRabatteChange);
      }
      if (this.boundRabatteClick) {
        document.removeEventListener('click', this.boundRabatteClick);
      }
      if (this.boundRabatteActionClick) {
        document.removeEventListener('click', this.boundRabatteActionClick);
      }
      document.body.classList.remove(SCROLL_LOCK_CLASS);
    }

    iconHTML(selector) {
      const tpl = this.querySelector(selector);
      return tpl ? tpl.innerHTML : '';
    }

    collectTabs() {
      const sources = this.querySelectorAll('[data-tab-v2-source]');
      this.tabs = Array.from(sources)
        .map((src) => {
          src.removeAttribute('hidden');
          this.bindUpsellToggles(src);
          return {
            id: src.getAttribute('data-tab-v2-id'),
            title: src.getAttribute('data-tab-v2-title') || '',
            titleHtml: src.getAttribute('data-tab-v2-title-html') || '',
            tabType: src.getAttribute('data-tab-v2-tab-type') || '',
            hiddenTrigger: src.getAttribute('data-tab-v2-hidden-trigger') === 'true',
            sourceEl: src,
          };
        })
        .filter((t) => t.id && t.title);
    }

    refreshFromHTML(html) {
      const template = document.createElement('template');
      template.innerHTML = html.trim();
      const newSources = template.content.querySelectorAll('[data-tab-v2-source]');

      newSources.forEach((newSourceEl) => {
        const tabId = newSourceEl.getAttribute('data-tab-v2-id');
        const tab = this.tabs.find((t) => t.id === tabId);
        if (!tab) return;

        const isActive = this.activeIndex >= 0 && this.tabs[this.activeIndex] === tab;
        const container = isActive ? this.panel : tab.sourceEl;

        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
        while (newSourceEl.firstChild) {
          container.appendChild(newSourceEl.firstChild);
        }

        this.bindUpsellToggles(container);
        if (tab.tabType === 'rabatte') {
          this.bindRabatteRadioGroups(container);
        }
      });
    }

    renderTriggers() {
      if (!this.triggerList) return;
      this.triggerList.innerHTML = '';
      this.tabs.forEach((tab, idx) => {
        if (tab.hiddenTrigger) return;
        const li = document.createElement('li');
        li.className = 'tab-drawer-v2__trigger-item';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tab-drawer-v2__trigger';
        btn.setAttribute('data-tab-target', tab.id);
        btn.setAttribute('data-tab-index', String(idx));
        btn.setAttribute('aria-expanded', 'false');
        btn.innerHTML = `
          <h5 class="tab-drawer-v2__trigger-label"></h5>
          <span class="tab-drawer-v2__trigger-icon" aria-hidden="true">${this.iconArrow}</span>
        `;
        const labelEl = btn.querySelector('.tab-drawer-v2__trigger-label');
        if (tab.titleHtml) {
          labelEl.innerHTML = tab.titleHtml;
        } else {
          labelEl.textContent = tab.title;
        }
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(tab.id);
        });
        li.appendChild(btn);
        this.triggerList.appendChild(li);
      });
    }

    renderNav() {
      if (!this.navList) return;
      this.navList.innerHTML = '';
      this.navList.setAttribute('role', 'tablist');
      this.tabs.forEach((tab, idx) => {
        // Rabatte tab: render in nav but hidden initially; regular hidden-trigger tabs: skip entirely
        if (tab.hiddenTrigger && tab.tabType !== 'rabatte') return;

        const li = document.createElement('li');
        li.className = 'tab-drawer-v2__nav-item';
        if (tab.tabType === 'rabatte') {
          li.classList.add('tab-drawer-v2__nav-item--rabatte');
          li.classList.add('is-hidden-nav');
        }
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'tab-drawer-v2__nav-link';
        btn.setAttribute('role', 'tab');
        btn.setAttribute('aria-selected', 'false');
        btn.setAttribute('data-tab-target', tab.id);
        btn.setAttribute('data-tab-index', String(idx));
        if (tab.tabType === 'rabatte') {
          btn.setAttribute('data-tab-type', 'rabatte');
        }
        btn.textContent = tab.title;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.setActive(idx);
        });
        li.appendChild(btn);
        this.navList.appendChild(li);
      });
    }

    bindCloseHandlers() {
      this.querySelectorAll('[data-tdv2-close]').forEach((btn) => {
        if (!btn.hasAttribute('aria-label')) {
          btn.setAttribute('aria-label', 'Close');
        }
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.close();
        });
      });
    }

    open(id) {
      const idx = this.tabs.findIndex((t) => t.id === id);
      if (idx === -1) return;

      const wasOpen = this.classList.contains('is-open');
      this.setActive(idx);

      if (!wasOpen) {
        this.previousFocus = document.activeElement;
        if (this.previousFocus && this.previousFocus.matches('.tab-drawer-v2__trigger')) {
          this.previousFocus.setAttribute('aria-expanded', 'true');
        }
        this.classList.add('is-open');
        this.setAttribute('data-state', 'open');
        document.body.classList.add(SCROLL_LOCK_CLASS);
        document.addEventListener('keydown', this.boundOnKeydown);
        requestAnimationFrame(() => this.focusFirst());
      }
    }

    openTabType(type) {
      const tab = this.tabs.find((t) => t.tabType === type);
      if (!tab) {
        return false;
      }
      this.open(tab.id);
      return true;
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('data-state', 'closed');
      document.body.classList.remove(SCROLL_LOCK_CLASS);
      document.removeEventListener('keydown', this.boundOnKeydown);
      this.returnPanelToSource();
      
      if (this.previousFocus && this.previousFocus.matches('.tab-drawer-v2__trigger')) {
        this.previousFocus.setAttribute('aria-expanded', 'false');
      }
      
      if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
        this.previousFocus.focus({ preventScroll: true });
      }
    }

    returnPanelToSource() {
      if (this.activeIndex < 0) return;
      const prev = this.tabs[this.activeIndex];
      if (!prev) return;
      while (this.panel.firstChild) {
        prev.sourceEl.appendChild(this.panel.firstChild);
      }
      this.activeIndex = -1;
    }

    setActive(idx) {
      const tab = this.tabs[idx];
      if (!tab) return;
      if (this.activeIndex === idx) return;

      if (this.activeIndex >= 0) {
        const prev = this.tabs[this.activeIndex];
        if (prev) {
          while (this.panel.firstChild) {
            prev.sourceEl.appendChild(this.panel.firstChild);
          }
        }
      }

      while (tab.sourceEl.firstChild) {
        this.panel.appendChild(tab.sourceEl.firstChild);
      }

      this.activeIndex = idx;
      if (this.activeTitleEl) this.activeTitleEl.textContent = tab.title;
      this.panel.scrollTop = 0;

      this.querySelectorAll('[data-tab-target]').forEach((el) => {
        const isActive = el.getAttribute('data-tab-target') === tab.id;
        el.classList.toggle('is-active', isActive);
        if (el.matches('.tab-drawer-v2__nav-link')) {
          el.setAttribute('aria-current', isActive ? 'true' : 'false');
          el.setAttribute('aria-selected', isActive ? 'true' : 'false');
        }
      });

      this.scrollActiveNavIntoView(tab.id);

      this.dispatchEvent(new CustomEvent('tab-drawer-v2:change', {
        detail: { id: tab.id, index: idx },
        bubbles: true,
      }));
    }

    scrollActiveNavIntoView(tabId) {
      if (!this.navList) return;
      const activeItem = this.navList.querySelector(
        `[data-tab-target="${tabId}"]`
      );
      if (!activeItem) return;
      const target = activeItem.closest('.tab-drawer-v2__nav-item') || activeItem;
      const itemLeft = target.offsetLeft;
      const itemRight = itemLeft + target.offsetWidth;
      const viewLeft = this.navList.scrollLeft;
      const viewRight = viewLeft + this.navList.clientWidth;

      let nextScroll = viewLeft;
      if (itemLeft < viewLeft) {
        nextScroll = itemLeft;
      } else if (itemRight > viewRight) {
        nextScroll = itemRight - this.navList.clientWidth;
      } else {
        return;
      }
      this.navList.scrollTo({ left: nextScroll, behavior: 'smooth' });
    }

    /* ---- Rabatte (subscription) integration ---- */

    setupRabatte() {
      const rabatte = this.tabs.find((t) => t.tabType === 'rabatte');
      if (!rabatte) return;
      this.rabatteTab = rabatte;

      // Mark body so CSS can hide inline selling-plan content
      // (body class survives selling-plan-picker DOM re-renders)
      document.body.classList.add('has-rabatte-drawer');

      // Listen for purchase-option radio changes
      this.boundRabatteChange = this.onPurchaseOptionChange.bind(this);
      this.boundRabatteClick = this.onSubscriptionLabelClick.bind(this);
      this.boundRabatteActionClick = this.onRabatteActionClick.bind(this);
      document.addEventListener('change', this.boundRabatteChange);
      document.addEventListener('click', this.boundRabatteClick);
      document.addEventListener('click', this.boundRabatteActionClick);

      // Check initial state
      const initialSub = document.querySelector('input[name="purchase-option"][type="radio"][value="subscription"]');
      if (initialSub && initialSub.checked) {
        this.showRabatteNav();
      }

      // Listen for radio buttons inside the rabatte drawer tab
      this.bindRabatteRadioGroups(rabatte.sourceEl);
    }

    bindRabatteRadioGroups(scope) {
      scope.querySelectorAll('[data-tdv2-rabatte-radio-group]').forEach((group) => {
        group.addEventListener('change', (e) => {
          const radio = e.target;
          if (radio && radio.type === 'radio') {
            this.syncSellingPlanFromDrawer(group, radio.value);
          }
        });
      });
    }

    onRabatteActionClick(e) {
      if (!e.target || !e.target.closest) return;
      const subscribeBtn = e.target.closest('[data-tdv2-rabatte-subscribe]');
      if (subscribeBtn) {
        e.preventDefault();
        this.onRabatteSubscribeClick(subscribeBtn);
        return;
      }
      const onetimeBtn = e.target.closest('[data-tdv2-rabatte-onetime]');
      if (onetimeBtn) {
        e.preventDefault();
        this.onRabatteOnetimeClick();
      }
    }

    onRabatteSubscribeClick(btn) {
      const groupId = btn.getAttribute('data-tdv2-rabatte-group');

      // Ensure the subscription radio for this group is checked
      const subRadio = groupId
        ? document.getElementById(`sellingplan-group-${groupId}`)
        : document.querySelector('input[name="purchase-option"][value="subscription"]');
      if (subRadio && !subRadio.checked) {
        subRadio.checked = true;
        subRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Re-sync the currently selected drawer radio to the form's selling_plan input
      const drawerGroup = btn.closest('.tab-rabatte-v2__group')
        ? btn.closest('.tab-rabatte-v2__group').querySelector('[data-tdv2-rabatte-radio-group]')
        : null;
      if (drawerGroup) {
        const checked = drawerGroup.querySelector('input[type="radio"]:checked');
        if (checked) this.syncSellingPlanFromDrawer(drawerGroup, checked.value);
      }

      this.close();

      const atc = document.querySelector(
        '.shopify-section-product-main .add-to-cart-button:not([disabled])'
      );
      if (atc) atc.click();
    }

    onRabatteOnetimeClick() {
      const oneTimeRadio = document.getElementById('onetime-purchase')
        || document.querySelector('input[name="purchase-option"][value="onetime"]');
      if (oneTimeRadio && !oneTimeRadio.checked) {
        oneTimeRadio.checked = true;
        oneTimeRadio.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        // Already onetime — just close the drawer
        this.hideRabatteNav();
        this.close();
      }
    }

    onPurchaseOptionChange(e) {
      const target = e.target;
      if (!target || !target.matches('input[name="purchase-option"][type="radio"]')) return;
      if (!target.checked) return;
      if (target.value === 'subscription') {
        this.showRabatteNav();
        this.openRabatte();
      } else {
        this.hideRabatteNav();
        this.close();
      }
    }

    onSubscriptionLabelClick(e) {
      const label = e.target && e.target.closest
        ? e.target.closest('label[for^="sellingplan-group-"]')
        : null;
      if (!label) return;
      const radio = document.getElementById(label.getAttribute('for'));
      if (radio && radio.checked) {
        this.showRabatteNav();
        this.openRabatte();
      }
    }

    openRabatte() {
      if (!this.rabatteTab) return;
      const isOpen = this.classList.contains('is-open');
      const isRabatteActive = this.activeIndex >= 0 && this.tabs[this.activeIndex] === this.rabatteTab;

      if (isOpen && isRabatteActive) {
        // Already showing rabatte — nothing to do
        return;
      }

      if (isOpen && !isRabatteActive) {
        // Drawer open on different tab — switch to rabatte
        this.setActive(this.tabs.indexOf(this.rabatteTab));
        return;
      }

      // Drawer is closed — reset activeIndex so setActive doesn't bail
      if (this.activeIndex >= 0 && this.tabs[this.activeIndex] === this.rabatteTab) {
        this.activeIndex = -1;
      }
      this.open(this.rabatteTab.id);
    }

    showRabatteNav() {
      if (!this.navList) return;
      const items = this.navList.querySelectorAll('.tab-drawer-v2__nav-item--rabatte');
      items.forEach((el) => el.classList.remove('is-hidden-nav'));
    }

    hideRabatteNav() {
      if (!this.navList) return;
      const items = this.navList.querySelectorAll('.tab-drawer-v2__nav-item--rabatte');
      items.forEach((el) => el.classList.add('is-hidden-nav'));
    }

    syncSellingPlanFromDrawer(group, newValue) {
      if (newValue == null) return;

      const mirrorId = group.getAttribute('data-tdv2-rabatte-mirror');
      if (mirrorId) {
        const original = document.getElementById(mirrorId);
        if (original && 'value' in original && original.value !== newValue) {
          original.value = newValue;
          original.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      const formInput = document.querySelector('selling-plan-picker input[name="selling_plan"]');
      if (formInput) {
        formInput.disabled = false;
        formInput.value = newValue;
        formInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Update displayed price in group name
      const checkedRadio = group.querySelector(`input[value="${newValue}"]`);
      if (checkedRadio) {
        const priceStr = checkedRadio.getAttribute('data-selling-plan-price');
        if (priceStr) {
          const groupEl = group.closest('.tab-rabatte-v2__group');
          if (groupEl) {
            const priceEl = groupEl.querySelector('.tab-rabatte-v2__group-name strong');
            if (priceEl) priceEl.textContent = priceStr;
          }
        }
      }
    }

    bindUpsellToggles(scope) {
      const triggers = scope.querySelectorAll('[data-upsell-target]');
      const panes = scope.querySelectorAll('[data-upsell-pane]');
      if (!triggers.length || !panes.length) return;
      triggers.forEach((trigger) => {
        if (trigger.dataset.tdv2Bound === '1') return;
        trigger.dataset.tdv2Bound = '1';
        trigger.addEventListener('click', () => {
          const target = trigger.getAttribute('data-upsell-target');
          triggers.forEach((t) => t.classList.toggle('is-active', t === trigger));
          panes.forEach((p) => {
            p.classList.toggle('is-active', p.getAttribute('data-upsell-pane') === target);
          });
        });
      });
    }

    onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (e.key === 'Tab') this.trapFocus(e);
    }

    focusFirst() {
      const focusable = this.drawer.querySelectorAll(FOCUSABLE);
      const target = focusable[0] || this.drawer;
      target.focus({ preventScroll: true });
    }

    trapFocus(e) {
      const focusable = Array.from(this.drawer.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null,
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('tab-drawer-v2', TabDrawerV2);

  function visibleDrawer() {
    const drawers = Array.from(document.querySelectorAll('tab-drawer-v2'));
    return drawers.find((el) => el.offsetParent !== null) || null;
  }

  document.addEventListener('click', (e) => {
    const el = e.target.closest('[data-open-tab-type], a[href="#product-reviews-tab"]');
    if (!el) return;

    const type = el.getAttribute('data-open-tab-type') || 'reviews';
    const drawer = visibleDrawer();
    if (!drawer || typeof drawer.openTabType !== 'function') return;
    if (!drawer.openTabType(type)) return;

    e.preventDefault();
    e.stopPropagation();
  }, true);

  function openFromHash() {
    if (window.location.hash !== '#reviews') return;
    const drawer = visibleDrawer();
    if (drawer && typeof drawer.openTabType === 'function') drawer.openTabType('reviews');
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', openFromHash);
  } else {
    openFromHash();
  }
  window.addEventListener('hashchange', openFromHash);
})();
